/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/BreakfastEventCompaniesTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./index.module.css";

import { ReactComponent as CompaniesIcon } from "../../assets/icons/SideMenu/CompaniesIcon.svg";

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

export const rowId = (company, index) => {
  if (company?.idEmpresa != null && company.idEmpresa !== "") {
    return String(company.idEmpresa);
  }
  return `row-${index}`;
};

const BreakfastEventCompaniesTable = ({
  companies,
  selectedIds,
  onSelectedIdsChange,
  // sortConfig,
  // onSort,
  renderArrow,
  onViewProfile,
  onRejectInvitation,
}) => {
  const { t } = useTranslation();
  const selectAllRef = useRef(null);

  const visibleRowIds = useMemo(
    () => companies.map((c, i) => rowId(c, i)),
    [companies],
  );

  const allSelected =
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectedIds.has(id));
  const someSelected = visibleRowIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) {
      return;
    }
    el.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const handleRejectInvitation = (company, index) => {
    const id = rowId(company, index);
    onSelectedIdsChange((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    onRejectInvitation(company, index);
  };

  const toggleRow = (company, index) => {
    const id = rowId(company, index);
    onSelectedIdsChange((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    onSelectedIdsChange((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleRowIds.forEach((id) => next.delete(id));
      } else {
        visibleRowIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  return (
    <div className={styles.usersTableContainer}>
      {/* Desktop/Laptop Table View */}
      <div className={styles.desktopView}>
        <table className={styles.usersTable}>
          <thead>
            <tr>
              <th className={styles.checkboxHeader}>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className={styles.rowCheckbox}
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label={t(
                    "components.breakfastEventCompaniesTable.selectAllRows",
                  )}
                />
              </th>
              <th className={styles.notificationSubjectHeader}>
                <div className={styles.sortableHeader}>
                  {t("components.breakfastEventCompaniesTable.companyName")}{" "}
                  {renderArrow()}
                </div>
              </th>
              <th className={styles.actionsHeader}>
                {t("components.breakfastEventCompaniesTable.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => {
              const id = rowId(company, index);
              const isSelected = selectedIds.has(id);
              return (
                <tr
                  key={id}
                  className={isSelected ? styles.rowSelected : undefined}
                >
                  <td className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      className={styles.rowCheckbox}
                      checked={isSelected}
                      onChange={() => toggleRow(company, index)}
                      aria-label={t(
                        "components.breakfastEventCompaniesTable.selectRow",
                      )}
                    />
                  </td>
                  <td className={`${styles.notificationSubjectData}`}>
                    <span className={styles.notificationSubjectText}>
                      {company.nombreComercial}
                    </span>
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actionButtonsGroup}>
                      <button
                        className={styles.cancelButton}
                        disabled={selectedIds.has(id)}
                        onClick={() => handleRejectInvitation(company, index)}
                      >
                        {t("components.breakfastEventCompaniesTable.reject")}
                      </button>
                      <button
                        className={styles.actionButton}
                        onClick={() => onViewProfile(company)}
                      >
                        <CompaniesIcon />
                        {t(
                          "components.breakfastEventCompaniesTable.seeProfile",
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BreakfastEventCompaniesTable;
